import { useState } from "react";
import { ArrowRight, ArrowLeft, Users, Compass, Cpu } from "lucide-react";

const SLIDES = [
  {
    id: "vinculos",
    eyebrow: "Modo · 01",
    title: "Vínculos",
    subtitle: "Tu círculo real, sin algoritmos.",
    body: "Familia, amigos y comunidades íntimas. Cronología pura. Lo que comparten tus personas llega cuando lo comparten — sin curaduría que decida por ti.",
    chips: ["orden cronológico", "círculos privados", "cero recomendaciones"],
    ringClass: "from-coral via-coral-hover to-ink",
    icon: Users,
    accent: "#E07A5F",
  },
  {
    id: "horizonte",
    eyebrow: "Modo · 02",
    title: "Horizonte",
    subtitle: "Descubre el mundo, con transparencia.",
    body: "Creadores, ideas, oportunidades, viajes. Una IA cultural que te explica por qué te recomienda algo y un botón claro para no verlo más.",
    chips: ["IA explicable", "180 países", "tú decides el filtro"],
    ringClass: "from-ink via-ink-mid to-coral",
    icon: Compass,
    accent: "#3D6FB5",
  },
  {
    id: "switch",
    eyebrow: "Modo · 03",
    title: "Cambias con un toque",
    subtitle: "Un gesto. Dos mundos.",
    body: "Pasas de Vínculos a Horizonte cuando tú decides. Ningún algoritmo te empuja a quedarte. Tu atención no se vende — se respeta.",
    chips: ["control total", "privacidad por diseño", "tu tiempo tiene valor"],
    ringClass: "from-coral via-ink to-coral",
    icon: Cpu,
    accent: "#0F1B2D",
  },
];

export default function TutorialStep({ onNext, onPrev }) {
  const [i, setI] = useState(0);
  const slide = SLIDES[i];
  const Icon = slide.icon;

  const goNext = () => {
    if (i < SLIDES.length - 1) setI(i + 1);
    else onNext();
  };
  const goPrev = () => {
    if (i > 0) setI(i - 1);
    else onPrev();
  };

  return (
    <div data-testid={`tutorial-slide-${slide.id}`} className="grid grid-cols-12 gap-8 items-center">
      {/* Visual panel */}
      <div className="col-span-12 md:col-span-6 order-2 md:order-1">
        <div className="relative aspect-square max-w-md mx-auto rounded-3xl overflow-hidden border hairline bg-ink p-10 flex items-center justify-center">
          <div className="absolute inset-0 opacity-25" style={{ background: `radial-gradient(600px 400px at 30% 20%, ${slide.accent} 0%, transparent 60%)` }} />
          <div className={`absolute inset-0 opacity-20 mix-blend-screen bg-gradient-to-br ${slide.ringClass}`} />

          <div className="relative z-10 flex flex-col items-center gap-6">
            <div className={`w-32 h-32 rounded-full p-[3px] bg-gradient-to-br ${slide.ringClass} animate-shimmer`}>
              <div className="w-full h-full rounded-full bg-ink flex items-center justify-center">
                <Icon className="w-12 h-12 text-cream" strokeWidth={1.6} />
              </div>
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-cream/65">{slide.eyebrow}</p>
            <p className="font-serif text-5xl text-cream tracking-tighter">{slide.title}</p>
          </div>

          {/* dots inside the visual */}
          <div className="absolute bottom-5 inset-x-0 flex justify-center gap-1.5">
            {SLIDES.map((_, idx) => (
              <span key={idx} className={`h-1 rounded-full transition-all ${idx === i ? "w-6 bg-coral" : "w-1 bg-cream/25"}`} />
            ))}
          </div>
        </div>
      </div>

      {/* Text panel */}
      <div className="col-span-12 md:col-span-6 order-1 md:order-2 reveal-child">
        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-coral">
          ◊ Tutorial · Feed Dual
        </p>
        <h2 className="mt-3 font-serif text-5xl md:text-6xl tracking-tighter leading-[0.95]">
          {slide.title}
        </h2>
        <p className="mt-2 font-serif italic text-2xl md:text-3xl text-ink-mid">
          {slide.subtitle}
        </p>
        <p className="mt-5 text-base md:text-lg text-ink/80 leading-relaxed text-balance">
          {slide.body}
        </p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {slide.chips.map((c) => (
            <li key={c} className="text-xs px-3 py-1.5 rounded-full bg-cream-deep text-ink-mid font-mono uppercase tracking-wider">
              {c}
            </li>
          ))}
        </ul>

        <div className="mt-8 flex items-center gap-3">
          <button
            onClick={goPrev}
            data-testid="tutorial-prev"
            className="inline-flex items-center gap-2 border hairline rounded-full px-5 py-2.5 text-sm hover:border-ink transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Atrás
          </button>
          <button
            onClick={goNext}
            data-testid="tutorial-next"
            className="inline-flex items-center gap-2 bg-ink text-cream rounded-full px-6 py-2.5 text-sm hover:bg-coral transition"
          >
            {i < SLIDES.length - 1 ? "Siguiente" : "Continuar al reto"}
            <ArrowRight className="w-4 h-4" />
          </button>
          <span data-testid="tutorial-progress" className="ml-auto font-mono text-[10px] uppercase tracking-[0.3em] text-ink-mid">
            {i + 1} / {SLIDES.length}
          </span>
        </div>
      </div>
    </div>
  );
}
